import { safeJSONParse } from '@utils/parse/json'
import { useCallback, useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

const isBrowser = typeof window !== 'undefined'

interface StoredValue<T> {
  value: T
  timestamp: number
}

/**
 * A hook to read and write to localStorage.
 * This hook is NOT debounced and writes directly to localStorage on every change.
 * For a debounced version, see `useDebouncedLocalStorage`.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
  options?: { ttl?: number },
): [T, (value: T | ((val: T) => T)) => void, () => void] {
  const { ttl } = options ?? {}

  const readValue = useCallback((): T => {
    if (!isBrowser) {
      return initialValue
    }

    try {
      const item = window.localStorage.getItem(key)
      if (!item) {
        return initialValue
      }

      const stored = safeJSONParse(item) as StoredValue<T> | null
      if (!stored) {
        return initialValue
      }

      if (ttl) {
        const now = Date.now()
        if (now - stored.timestamp > ttl) {
          window.localStorage.removeItem(key)
          return initialValue
        }
      }

      return stored.value
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error)
      return initialValue
    }
  }, [initialValue, key, ttl])

  const [storedValue, setStoredValue] = useState<T>(readValue)

  const setValue = useCallback(
    (value: T | ((val: T) => T)) => {
      if (!isBrowser) {
        console.warn(
          `Tried to set localStorage key "${key}" even though environment is not a browser.`,
        )
        return
      }

      try {
        const newValue = value instanceof Function ? value(storedValue) : value
        const itemToStore = {
          value: newValue,
          timestamp: Date.now(),
        }
        window.localStorage.setItem(key, JSON.stringify(itemToStore))
        setStoredValue(newValue)
        window.dispatchEvent(new StorageEvent('storage', { key }))
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error)
      }
    },
    [key, storedValue],
  )

  useEffect(() => {
    setStoredValue(readValue())
  }, [readValue])

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key || e.key === null) {
        setStoredValue(readValue())
      }
    }

    if (isBrowser) {
      window.addEventListener('storage', handleStorageChange)
    }

    return () => {
      if (isBrowser) {
        window.removeEventListener('storage', handleStorageChange)
      }
    }
  }, [key, readValue])

  const reset = useCallback(() => {
    if (!isBrowser) {
      console.warn(
        `Tried to reset localStorage key "${key}" even though environment is not a browser.`,
      )
      return
    }

    try {
      window.localStorage.removeItem(key)
      setStoredValue(initialValue)
      window.dispatchEvent(new StorageEvent('storage', { key }))
    } catch (error) {
      console.warn(`Error resetting localStorage key "${key}":`, error)
    }
  }, [key, initialValue])

  return [storedValue, setValue, reset]
}

/**
 * A debounced version of useLocalStorage that delays writes to localStorage.
 * @param key The key to use in localStorage.
 * @param initialValue The initial value to use if nothing is in localStorage or the stored value is expired.
 * @param options.ttl Time-to-live in milliseconds.
 * @param options.debounceMs Debounce delay in milliseconds. Defaults to 500ms.
 * @returns A stateful value, and a function to update it.
 */
export function useDebouncedLocalStorage<T>(
  key: string,
  initialValue: T,
  options?: { ttl?: number; debounceMs?: number },
): [T, (value: T | ((val: T) => T)) => void, () => void] {
  const readValue = useCallback((): T => {
    if (!isBrowser) {
      return initialValue
    }
    try {
      const item = window.localStorage.getItem(key)
      if (!item) return initialValue

      const stored = safeJSONParse(item) as StoredValue<T> | null
      if (!stored) return initialValue

      if (options?.ttl) {
        const now = Date.now()
        if (now - stored.timestamp > options.ttl) {
          window.localStorage.removeItem(key)
          return initialValue
        }
      }
      return stored.value
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error)
      return initialValue
    }
  }, [initialValue, key, options?.ttl])

  const [localState, setLocalState] = useState(readValue)
  const [debouncedValue] = useDebounce(localState, options?.debounceMs ?? 500)

  useEffect(() => {
    const itemToStore = {
      value: debouncedValue,
      timestamp: Date.now(),
    }
    window.localStorage.setItem(key, JSON.stringify(itemToStore))
  }, [debouncedValue, key])

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key) {
        setLocalState(readValue())
      }
    }
    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [key, readValue])

  const reset = useCallback(() => {
    window.localStorage.removeItem(key)
    setLocalState(initialValue)
  }, [key, initialValue])

  return [localState, setLocalState, reset]
}
